import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import type { WorkOrderPresenter } from './workOrderPresentation'
import { createChatDisplayPresenter } from './chatDisplayPresenter'

export const ChatDisplayContext = createContext<
  | (WorkOrderPresenter &
      Partial<
        Pick<
          ReturnType<typeof createChatDisplayPresenter>,
          'subscribe' | 'getRevision' | 'resolveDisplayed' | 'isCurrent'
        >
      >)
  | undefined
>(undefined)
export const ChatDisplayExcluded = createContext(false)
const EXACT_IDENTITY = /^(?:code|unit|partNumber|woNumber|orderNumber)$/
const IDENTITY =
  /^(?:manufacturer|model|equipment[_A-Z]?(?:Tag|Name|tag|name|code)|businessId|sequentialNumber|technicianName|userName|teamName|operationTeamName|plantName|locationName|part_number|plant_name|location_name)$/
function identities(
  value: unknown,
  result: string[] = [],
  depth = 0,
  parent = '',
  exact = false,
): string[] {
  if (!value || typeof value !== 'object' || depth > 8) return result
  const field = value as { label?: unknown; value?: unknown }
  recordIdentity(field.value, field.label, parent, exact, result)
  for (const [key, item] of Object.entries(value)) {
    recordIdentity(item, key, parent, exact, result)
    if (typeof item === 'object')
      identities(item, result, depth + 1, /^\d+$/.test(key) ? parent : key, exact)
  }
  return result
}
function recordIdentity(
  value: unknown,
  key: unknown,
  parent: string,
  exact: boolean,
  result: string[],
) {
  if (typeof value === 'string' && value && isIdentity(key, parent, exact)) result.push(value)
}
function isIdentity(key: unknown, parent = '', exact = false): boolean {
  return (
    typeof key === 'string' &&
    (exact
      ? EXACT_IDENTITY.test(key)
      : IDENTITY.test(key) ||
        (key === 'name' && /technician|assignee|user|person|team/i.test(parent)))
  )
}

/** Render-only scope: original message objects and business callbacks remain untouched. */
export const ChatDisplayScopePresent = createContext(false)
export const ChatDisplayStreaming = createContext(false)

export function ChatDisplayScope({
  message,
  children,
}: {
  message: { conversationId: string; richContent?: unknown; isStreaming?: boolean }
  children: ReactNode
}) {
  const tenantId = useAuthStore((state) => state.tenantId)
  const userId = useAuthStore((state) => state.userId)
  const { i18n } = useTranslation()
  const locale = i18n.resolvedLanguage ?? i18n.language
  const namesKey = JSON.stringify({
    names: identities(message.richContent),
    exact: identities(message.richContent, [], 0, '', true),
  })
  const scoped = useMemo(() => {
    const names = JSON.parse(namesKey) as { names: string[]; exact: string[] }
    return createChatDisplayPresenter({ tenantId, userId }, names.names, names.exact)
  }, [tenantId, userId, message.conversationId, namesKey, locale])
  useEffect(() => {
    scoped.retain()
    return () => scoped.release()
  }, [scoped])
  const active =
    !message.isStreaming &&
    typeof tenantId === 'string' &&
    typeof userId === 'string' &&
    Boolean(tenantId && userId)
  return (
    <ChatDisplayScopePresent.Provider value={true}>
      <ChatDisplayStreaming.Provider value={Boolean(message.isStreaming)}>
        <ChatDisplayContext.Provider value={active ? scoped : undefined}>
          {children}
        </ChatDisplayContext.Provider>
      </ChatDisplayStreaming.Provider>
    </ChatDisplayScopePresent.Provider>
  )
}

export const useChatDisplay = () => useContext(ChatDisplayContext)
